import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Loader2, Save, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { integrations, waIntegration } from "@/lib/api";

const providerMeta = {
  fonnte: { name: "Fonnte", desc: "Lokal Indonesia. Cocok untuk UMKM. Signup di fonnte.com dan scan WA Anda.", url: "https://fonnte.com" },
  twilio: { name: "Twilio", desc: "Global, developer-friendly. Support WhatsApp Business API resmi. Free trial tersedia.", url: "https://twilio.com/whatsapp" },
  meta: { name: "Meta WhatsApp Cloud API", desc: "Langsung dari Meta. 1000 conversation gratis/bulan. Butuh WhatsApp Business Account.", url: "https://developers.facebook.com/docs/whatsapp/cloud-api" },
};

export default function WAIntegration() {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("fonnte");
  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState({ token: "", account_sid: "", auth_token: "", from_number: "", phone_number_id: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const d = await integrations.getWA();
      setData(d);
      setEnabled(!!d.enabled);
      setProvider(d.provider || "fonnte");
    } catch (err) { console.warn("WA integration load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { provider, enabled };
      if (form.token) payload.token = form.token;
      if (provider === "twilio") {
        if (form.account_sid) payload.account_sid = form.account_sid;
        if (form.auth_token) { payload.auth_token = form.auth_token; payload.token = form.auth_token; }
        if (form.from_number) payload.from_number = form.from_number;
      }
      if (provider === "meta") {
        if (form.phone_number_id) payload.phone_number_id = form.phone_number_id;
      }
      await waIntegration.update(payload);
      toast.success(`Integrasi ${providerMeta[provider].name} disimpan`);
      setForm({ token: "", account_sid: "", auth_token: "", from_number: "", phone_number_id: "" });
      fetchData();
    } catch (err) { console.warn("WA save failed:", err); toast.error("Gagal simpan"); }
    setSaving(false);
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Integrasi WhatsApp</h1>
        <p className="text-sm text-gray-500 mt-1">Pilih provider WhatsApp Business API untuk kirim campaign otomatis.</p>
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <Tabs value={provider} onValueChange={setProvider}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="fonnte" data-testid="tab-fonnte">Fonnte</TabsTrigger>
              <TabsTrigger value="twilio" data-testid="tab-twilio">Twilio</TabsTrigger>
              <TabsTrigger value="meta" data-testid="tab-meta">Meta Cloud</TabsTrigger>
            </TabsList>

            <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
              <span>Status:</span>
              {data?.provider === provider && data?.enabled && data?.token ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border"><CheckCircle2 size={12} className="mr-1" /> Connected</Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-600 border-gray-200 border"><XCircle size={12} className="mr-1" /> Belum connect</Badge>
              )}
            </div>

            <p className="mt-4 text-sm text-gray-600">{providerMeta[provider].desc}{" "}<a href={providerMeta[provider].url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">Info →</a></p>

            <TabsContent value="fonnte" className="mt-5 space-y-4">
              <div>
                <Label>Device Token Fonnte</Label>
                <Input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder={data?.token_masked || "Paste dari Fonnte dashboard"} data-testid="fonnte-token-input" />
              </div>
            </TabsContent>

            <TabsContent value="twilio" className="mt-5 space-y-4">
              <div><Label>Account SID</Label><Input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="AC..." data-testid="twilio-sid" /></div>
              <div><Label>Auth Token</Label><Input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="Paste dari Twilio console" data-testid="twilio-token" /></div>
              <div><Label>WhatsApp Sender Number</Label><Input value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} placeholder="+14155238886 (sandbox) atau nomor bisnis" data-testid="twilio-from" /></div>
            </TabsContent>

            <TabsContent value="meta" className="mt-5 space-y-4">
              <div><Label>Access Token</Label><Input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="Paste dari Meta Business Manager" data-testid="meta-token" /></div>
              <div><Label>Phone Number ID</Label><Input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} placeholder="Contoh: 123456789012345" data-testid="meta-phone-id" /></div>
            </TabsContent>

            <div className="mt-5 border-t border-gray-100 pt-5 flex items-center gap-3">
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="wa-enable" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Aktifkan untuk campaign</div>
                <div className="text-xs text-gray-500">Kalau OFF, campaign tetap generate wa.me links (mode manual)</div>
              </div>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-wa-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Simpan
              </Button>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
